import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Tabs } from 'tea-component';
import { useMemoizedFn } from 'ahooks';
import { useIntl, useLocation } from 'umi';
import NewMarkIcon from '@home/components/NewMarkIcon';
import { getBasePath } from '@/utils/utils';
import { isUrlMatch, normalizeBaseUrlTemplate } from '@intelligence/components/MarqueeBanner/useAd';
import { fetchAd } from '@intelligence/services/common';
// 旧 tab NEW 方案已停用：不再通过 newMarksState + newMarkId 控制 tab 标签。
// import useNewMark, { addTabNewIcon } from '@intelligence/hooks/useNewMark';
// import { TABS_NEW_MARK_IDS_MAPPING } from '@intelligence/_config/newMarks';

import type { TabsProps, Tab } from 'tea-component';

interface INewIconTabsProps extends TabsProps {
  // 旧 tab NEW 方案的映射 key，当前 newicon 动态方案不再依赖该字段。
  newMarkIdsMappingKey?: string;
}

const DEFAULT_NEW_ICON_TEXT = 'NEW';

/**
 * newicon 列表获取策略：
 * - 与跑马灯一致："进入新页面（pathname 变化）就请求一次"，但不传 url，全量拉。
 * - 模块级 cache 跨组件共享，作为 useState 的初值，保证组件 mount 第一帧就有数据，
 *   避免出现 "[] -> 接口回来才有" 的空帧闪烁。
 * - 接口返回若与上次相同（深比对），不触发 setState，避免不必要的 re-render。
 * - 同 pathname 下的多个 NewIconTabs 共享同一次请求（in-flight Promise 复用），避免重复请求。
 */
let newIconListCache: any[] = [];
const inflightRequests = new Map<string, Promise<any[]>>();

const parseNewIconContent = (content: any): any => {
  if (!content) return {};
  if (typeof content !== 'string') return content;
  try {
    return JSON.parse(content);
  } catch (error) {
    console.debug('[NewIconTabs] invalid content json:', error);
    return {};
  }
};

const filterValidNewIcons = (list: any[]): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const now = Date.now();
  return list.reduce<any[]>((acc, item) => {
    if (!item) return acc;
    if (item.type && item.type !== 'newicon') return acc;
    const start = item.start_time ? new Date(item.start_time).getTime() : NaN;
    const end = item.end_time ? new Date(item.end_time).getTime() : NaN;
    if (!Number.isNaN(start) && now < start) return acc;
    if (!Number.isNaN(end) && now > end) return acc;
    return [...acc, { ...item, content: parseNewIconContent(item?.content) }];
  }, []);
};

const isSameNewIconList = (a: any[], b: any[]): boolean => JSON.stringify(a || []) === JSON.stringify(b || []);

const fetchNewIconListByPath = (pathKey: string): Promise<any[]> => {
  const inflight = inflightRequests.get(pathKey);
  if (inflight) return inflight;
  const promise = fetchAd({ type: 'newicon' })
    .then((res) => {
      const raw = res?.code === 0 && Array.isArray(res?.data) ? res.data : [];
      const next = filterValidNewIcons(raw);
      if (!isSameNewIconList(newIconListCache, next)) {
        newIconListCache = next;
      }
      return newIconListCache;
    })
    .catch((e) => {
      console.debug('[NewIconTabs] fetch newicon failed:', e);
      return newIconListCache;
    })
    .finally(() => {
      inflightRequests.delete(pathKey);
    });
  inflightRequests.set(pathKey, promise);
  return promise;
};

/**
 * 进入新页面就请求一次：
 * - mount 第一帧用模块级 cache，不闪；
 * - pathname 变化时静默重新拉一次，结果与本地 list 不同才 setState。
 */
const useNewIconList = (pathKey: string): any[] => {
  const [list, setList] = useState<any[]>(newIconListCache);
  const listRef = useRef<any[]>(newIconListCache);

  useEffect(() => {
    let mounted = true;
    fetchNewIconListByPath(pathKey).then((next) => {
      if (!mounted) return;
      // 与本地当前 list 比对，相同则不 setState，避免无谓 re-render 触发 Tabs 闪烁
      if (isSameNewIconList(listRef.current, next)) return;
      listRef.current = next;
      setList(next);
    });
    return () => {
      mounted = false;
    };
  }, [pathKey]);

  return list;
};

const getNewIconText = (item: any, locale?: string): string => {
  const content = item?.content || {};
  const isZh = locale?.startsWith('zh');
  const primaryText = isZh ? content?.text_cn : content?.text_en;
  const fallbackText = isZh ? content?.text_en : content?.text_cn;
  const text = [primaryText, fallbackText].find((itemText) => typeof itemText === 'string' && !!itemText.trim());
  return text?.trim() || DEFAULT_NEW_ICON_TEXT;
};

const getUrlSegments = (url: string): string[] => url.split('/').map((item) => decodeURIComponent(item));

/** 定位 URL 中一级 Tab 段（tab.id）的下标 */
const findPrimaryTabIndex = (url: string, knownTabIds: string[]): number => {
  if (!url || !knownTabIds.length) return -1;
  const decodedSegments = getUrlSegments(url);
  for (let i = decodedSegments.length - 1; i >= 0; i -= 1) {
    if (knownTabIds.includes(decodedSegments[i])) {
      return i;
    }
  }
  const gamesDetailsIndex = decodedSegments.lastIndexOf('gamesDetails');
  if (gamesDetailsIndex >= 0 && gamesDetailsIndex + 2 < decodedSegments.length) {
    return gamesDetailsIndex + 2;
  }
  return -1;
};

/**
 * 按 content.tab 中的路径（如 stats、countries/activeusers）构造完整 URL。
 * 仅替换 Tab 段及其后的 segment，前缀（含 game_id）沿用当前页。
 */
const buildUrlWithTabPath = (currentUrl: string, knownTabIds: string[], tabPath: string): string => {
  if (!currentUrl || !tabPath) return currentUrl;
  const segments = currentUrl.split('/');
  const tabIndex = findPrimaryTabIndex(currentUrl, knownTabIds);
  if (tabIndex < 0) return currentUrl;
  const pathSegments = tabPath.split('/').filter(Boolean);
  return [...segments.slice(0, tabIndex), ...pathSegments].join('/');
};

/** content.tab 中与 UI Tab id 对应的后端路径（首段为 tab.id，如 stats、countries/activeusers） */
const getTabPathCandidates = (content: any, uiTabId: string): string[] => {
  if (!Array.isArray(content?.tab)) return [uiTabId];
  return content.tab
    .map((item: string) => String(item).trim())
    .filter(Boolean)
    .filter((path: string) => path.split('/')[0] === uiTabId);
};

/**
 * Tab 徽章匹配：用配置路径模拟 URL，与当前选中 Tab 无关。
 * - 一级路径（如 stats、countries）：模拟后匹配，未配置的子路由由 isUrlMatch 通配
 * - 多级路径（如 countries/activeusers）：模拟完整路径后精确匹配
 */
const doesTabPathQualifyForBadge = (
  baseUrl: string,
  content: any,
  currentUrl: string,
  knownTabIds: string[],
  uiTabId: string,
  tabPath: string,
): boolean => {
  if (tabPath.split('/')[0] !== uiTabId) return false;
  const simulatedUrl = buildUrlWithTabPath(currentUrl, knownTabIds, tabPath);
  return isUrlMatch(baseUrl, simulatedUrl, { ...content, tab: [tabPath] });
};

const getTabUrl = (currentUrl: string, activeId?: string, tabId?: string): string => {
  const activeIdTmp = String(activeId || '');
  const tabIdTmp = String(tabId || '');
  if (!currentUrl || !activeIdTmp || !tabIdTmp) return currentUrl;

  const segments = currentUrl.split('/');
  const index = getUrlSegments(currentUrl).lastIndexOf(activeIdTmp);
  if (index < 0) return currentUrl;

  const nextSegments = [...segments];
  nextSegments[index] = tabIdTmp;
  return nextSegments.join('/');
};

const getBaseUrlWithoutEnumRule = (baseUrl: string): string => normalizeBaseUrlTemplate(baseUrl);

const isSameTabGroupNewIcon = (baseUrl: string, currentUrl: string, activeId?: string, tabId?: string): boolean => {
  const activeIdTmp = String(activeId || '');
  const tabIdTmp = String(tabId || '');
  if (!baseUrl || !currentUrl || !activeIdTmp || !tabIdTmp) return false;
  if (baseUrl.includes('{')) return false;

  const currentSegments = getUrlSegments(currentUrl);
  const activeIndex = currentSegments.lastIndexOf(activeIdTmp);
  if (activeIndex < 0) return false;

  const baseSegments = getUrlSegments(getBaseUrlWithoutEnumRule(baseUrl));
  if (baseSegments.length <= activeIndex) return false;
  if (baseSegments[activeIndex] !== tabIdTmp) return false;

  const currentPrefix = currentSegments.slice(0, activeIndex).join('/');
  const basePrefix = baseSegments.slice(0, activeIndex).join('/');
  return isUrlMatch(basePrefix, currentPrefix);
};

const isNewIconMatchedTab = (
  item: any,
  currentUrl: string,
  knownTabIds: string[],
  activeId: string | undefined,
  uiTabId: string,
): boolean => {
  const baseUrl = item?.base_url || '';
  const content = item?.content || {};
  const hasTabPlaceholder = baseUrl.includes('{tab}');

  if (hasTabPlaceholder || Array.isArray(content?.tab)) {
    const tabPaths = getTabPathCandidates(content, uiTabId);
    if (Array.isArray(content?.tab) && !tabPaths.length) {
      return false;
    }
    return tabPaths.some((tabPath) =>
      doesTabPathQualifyForBadge(baseUrl, content, currentUrl, knownTabIds, uiTabId, tabPath),
    );
  }

  const tabUrl = getTabUrl(currentUrl, activeId, uiTabId);
  return isUrlMatch(baseUrl, tabUrl, content) || isSameTabGroupNewIcon(baseUrl, currentUrl, activeId, uiTabId);
};

/**
 * 为每个 tab 计算它应该显示的 NEW 文案。
 * - 命中 newicon 配置 -> 返回文案
 * - 没命中 -> 返回 ''
 */
const calcTabNewTexts = (
  tabs: Tab[],
  activeId: string | undefined,
  currentUrl: string,
  knownTabIds: string[],
  newIconList: any[],
  locale?: string,
): string[] => {
  if (!Array.isArray(tabs)) return [];
  if (!Array.isArray(newIconList) || !newIconList.length) return tabs.map(() => '');
  return tabs.map((tab) => {
    const tabId = String(tab?.id || '');
    const matched = newIconList.find((item) =>
      isNewIconMatchedTab(item, currentUrl, knownTabIds, activeId, tabId),
    );
    return matched ? getNewIconText(matched, locale) : '';
  });
};

const wrapTabWithNewIcon = (tab: Tab, newText: string): Tab => {
  if (!newText) return tab;
  return {
    ...(tab || {}),
    label: (
      <div className="dbi-tab-flex">
        {tab?.label}
        <NewMarkIcon text={newText} />
      </div>
    ),
  };
};

const NewIconTabs: React.FC<INewIconTabsProps> = (props) => {
  const { className, newMarkIdsMappingKey, tabs: tabsProps, onActive: onActiveProps, activeId, ...restProps } = props;
  void newMarkIdsMappingKey;

  const { pathname } = useLocation();
  const { locale } = useIntl();
  const currentUrl = `${getBasePath()}${pathname}`;
  const newIconList = useNewIconList(currentUrl);

  /**
   * 闪烁修复关键：
   * 父组件切 tab 时，`activeId` 来自 React state、`pathname` 来自 history，
   * 这两者**不在同一帧到达 NewIconTabs**：
   *   - 第一帧：activeId = 新 tab，pathname 仍是旧 tab → getTabUrl/isSameTabGroupNewIcon 命中失败 → NEW 消失
   *   - 第二帧：pathname 也更新成新 tab → 重新命中 → NEW 出现
   * 这就是 "先清空再出现" 的肉眼闪烁。
   *
   * 解决：忽略 props.activeId，统一从 currentUrl 中推断当前激活的 tab 段
   * （即 url 里和某个 tab.id 一致的那一段）。这样 currentUrl 与内部 activeId
   * 始终同源同帧变化，避免错位。
   */
  const tabIds = useMemo<string[]>(() => {
    if (!Array.isArray(tabsProps)) return [];
    return tabsProps.map((tab) => String(tab?.id || ''));
  }, [tabsProps]);

  const inferredActiveId = useMemo<string | undefined>(() => {
    const segments = getUrlSegments(currentUrl);
    // 从后往前找，优先匹配最深一级 tab 段，避免上层路径误命中
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (tabIds.includes(segments[i])) return segments[i];
    }
    return activeId ? String(activeId) : undefined;
  }, [currentUrl, tabIds, activeId]);

  const newTexts = useMemo(
    () =>
      calcTabNewTexts(
        Array.isArray(tabsProps) ? tabsProps : [],
        inferredActiveId,
        currentUrl,
        tabIds,
        newIconList,
        locale,
      ),
    [tabsProps, inferredActiveId, currentUrl, tabIds, newIconList, locale],
  );
  const newTextsKey = useMemo(() => newTexts.join('|'), [newTexts]);

  /**
   * 用 ref 缓存上一次的 tabs 数组：当 tabsProps 引用没变 + 文案签名没变时，
   * 直接返回上次的引用，避免父组件 render 但 NEW 状态实际未变时也触发 Tabs 重渲染。
   */
  const tabsCacheRef = useRef<{ source: Tab[] | undefined; key: string; result: Tab[] } | null>(null);
  const tabs = useMemo<Tab[]>(() => {
    if (!Array.isArray(tabsProps)) return [];
    const cache = tabsCacheRef.current;
    if (cache && cache.source === tabsProps && cache.key === newTextsKey) {
      return cache.result;
    }
    const result = tabsProps.map((tab, index) => wrapTabWithNewIcon(tab, newTexts[index] || ''));
    tabsCacheRef.current = { source: tabsProps, key: newTextsKey, result };
    return result;
  }, [tabsProps, newTextsKey, newTexts]);

  const handleActive = useMemoizedFn((tab: Tab, e: React.SyntheticEvent<Element, Event>) => {
    // 旧 tab NEW 方案已停用，不再写入 addNewMarkRecord 点击记录。
    onActiveProps?.(tab, e);
  });

  return (
    <Tabs
      {...restProps}
      activeId={activeId}
      className={classnames('dbh-new-icon-tabs dbi-detail-new-icon-tabs', className)}
      tabs={tabs}
      // @ts-ignore
      onActive={handleActive}
    />
  );
};

export default React.memo(NewIconTabs);
